import { z } from 'zod';
import type { PaymentStatus } from './booking.js';
import type { SubscriptionTierCode } from './subscription.js';

/**
 * F41 — Tournament & Event Listings frozen API contracts (Part A — listing /
 * division / registration / off-platform payment / hosting / organizer + Part B's
 * shared status enums; the in-play bracket/standings read shapes live in
 * `tournament-bracket.ts`).
 *
 * An organizer is a `b2b_accounts` row of `account_kind ∈ {venue, club, organizer}`.
 * Two enablement paths (§18.1):
 *   - admin-approved `organizer` accounts (federations / leagues) — no subscription
 *     gate, no listing fee, no quota,
 *   - self-serve `venue`/`club` accounts owning a Pro+ venue — gated via the F38
 *     `TierProvider` + `TIER_ACTIVE_TOURNAMENT_QUOTA` + the snapshotted listing fee.
 * An admin may also create on behalf of any organizer, bypassing all gates (D19).
 *
 * A tournament carries one-or-more sport-aware divisions (solo or team), per-division
 * AND per-tournament capacity caps (atomic counters), and hosts across one-or-more
 * venues/locations (the §18.2 hosting-approval table). Registration is off-platform:
 * the organizer marks each registrant paid → `confirmed`. F40 promo codes lower the
 * persisted `net_fee_amount_minor` via the F40 Option-A polymorphism.
 *
 * `payment_status` reuses F17's vocabulary (`PaymentStatus` from `booking.ts`) — it
 * is NOT redeclared here. The listing fee bills through F23's `commission_statements`
 * with the new `tournament_listing` source (see `commission.ts COMMISSION_SOURCES`).
 *
 * All monetary fields are USD minor units (integer, cents).
 *
 * See plans/41_F41_tournament_event_listings.md §§2/3/17/18. Wave-2 sub-agents
 * develop against these contracts in parallel — DO NOT rename/drop/add fields
 * without escalating to the orchestrator first (contract-amendment protocol).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * Tournament lifecycle status (§4). `cancelled` is terminal; admin suspension is
 * an ORTHOGONAL `suspendedAt` flag (NOT a status value — mirrors F32/F40).
 */
export const TOURNAMENT_STATUSES = [
  'draft',
  'published',
  'registration_open',
  'registration_closed',
  'live',
  'completed',
  'cancelled',
] as const;
export type TournamentStatus = (typeof TOURNAMENT_STATUSES)[number];
export const TournamentStatusSchema = z.enum(TOURNAMENT_STATUSES);

/**
 * The tournament-level headline format (M14 — a publish-locked denormalized
 * snapshot; `tournament_divisions.format` is the real per-division engine driver).
 * `'mixed'` is used when divisions differ; the three engine formats are the
 * `TOURNAMENT_DIVISION_FORMATS` set below.
 */
export const TOURNAMENT_HEADLINE_FORMATS = [
  'single_elimination',
  'round_robin',
  'league',
  'mixed',
] as const;
export type TournamentHeadlineFormat = (typeof TOURNAMENT_HEADLINE_FORMATS)[number];
export const TournamentHeadlineFormatSchema = z.enum(TOURNAMENT_HEADLINE_FORMATS);

/** The per-division bracket-engine format (D14, M14 — drives the in-play engine). */
export const TOURNAMENT_DIVISION_FORMATS = ['single_elimination', 'round_robin', 'league'] as const;
export type TournamentDivisionFormat = (typeof TOURNAMENT_DIVISION_FORMATS)[number];
export const TournamentDivisionFormatSchema = z.enum(TOURNAMENT_DIVISION_FORMATS);

/** Whether a division accepts solo entrants or teams (D11). */
export const TOURNAMENT_REGISTRATION_KINDS = ['solo', 'team'] as const;
export type TournamentRegistrationKind = (typeof TOURNAMENT_REGISTRATION_KINDS)[number];
export const TournamentRegistrationKindSchema = z.enum(TOURNAMENT_REGISTRATION_KINDS);

/** Optional per-division gender category (D11). */
export const TOURNAMENT_GENDER_CATEGORIES = ['men', 'women', 'mixed', 'open'] as const;
export type TournamentGenderCategory = (typeof TOURNAMENT_GENDER_CATEGORIES)[number];
export const TournamentGenderCategorySchema = z.enum(TOURNAMENT_GENDER_CATEGORIES);

/** Bracket seeding method, organizer's choice at generation (D18). */
export const TOURNAMENT_SEEDING_METHODS = ['manual', 'random', 'skill'] as const;
export type TournamentSeedingMethod = (typeof TOURNAMENT_SEEDING_METHODS)[number];
export const TournamentSeedingMethodSchema = z.enum(TOURNAMENT_SEEDING_METHODS);

/**
 * Registration lifecycle status (the widened F41 set — D12). `interested` is kept
 * ONLY for the inert F36 legacy stub rows (`tournament_id IS NULL`); every real
 * F41 registration carries a `tournament_id` and a non-`interested` status.
 *
 * NOTE: named `*_LIFECYCLE_STATUSES` / `TournamentRegistrationLifecycleStatus` to
 * avoid colliding with F36's narrow `TOURNAMENT_REGISTRATION_STATUS_VALUES`
 * (`['interested']`) + `TournamentRegistrationStatus` in `tournament-registration.ts`
 * (both re-exported via the barrel). The F41 set is the authoritative superset.
 */
export const TOURNAMENT_REGISTRATION_LIFECYCLE_STATUSES = [
  'interested',
  'pending_payment',
  'confirmed',
  'withdrawn',
  'refunded',
  'cancelled_with_tournament',
] as const;
export type TournamentRegistrationLifecycleStatus =
  (typeof TOURNAMENT_REGISTRATION_LIFECYCLE_STATUSES)[number];
export const TournamentRegistrationLifecycleStatusSchema = z.enum(
  TOURNAMENT_REGISTRATION_LIFECYCLE_STATUSES,
);

/** Announcement audience (§3). */
export const TOURNAMENT_ANNOUNCEMENT_AUDIENCES = ['all_registrants', 'confirmed_only'] as const;
export type TournamentAnnouncementAudience = (typeof TOURNAMENT_ANNOUNCEMENT_AUDIENCES)[number];
export const TournamentAnnouncementAudienceSchema = z.enum(TOURNAMENT_ANNOUNCEMENT_AUDIENCES);

/** Tournament location-kind discriminator (denormalized headline; the real venues
 * live in `tournament_venue_hosts`). */
export const TOURNAMENT_LOCATION_KINDS = ['venue', 'free_text'] as const;
export type TournamentLocationKind = (typeof TOURNAMENT_LOCATION_KINDS)[number];

/** Organizer-profile status (§18.1 — admin approves an organizer partner). */
export const ORGANIZER_PROFILE_STATUSES = ['pending', 'approved', 'suspended'] as const;
export type OrganizerProfileStatus = (typeof ORGANIZER_PROFILE_STATUSES)[number];
export const OrganizerProfileStatusSchema = z.enum(ORGANIZER_PROFILE_STATUSES);

/** Organizer profile type (§2.1 stub). */
export const ORGANIZER_TYPES = ['federation', 'standalone', 'venue', 'club'] as const;
export type OrganizerType = (typeof ORGANIZER_TYPES)[number];
export const OrganizerTypeSchema = z.enum(ORGANIZER_TYPES);

/** The organizer account-kind snapshot stamped on a tournament (`organizer_kind`). */
export const TOURNAMENT_ORGANIZER_KINDS = ['venue', 'club', 'organizer'] as const;
export type TournamentOrganizerKind = (typeof TOURNAMENT_ORGANIZER_KINDS)[number];

/** Venue-hosting-request status (§18.2, D20). A free-text host inserts `approved`;
 * a claimed-venue host inserts `requested` and the venue account decides. */
export const TOURNAMENT_VENUE_HOST_STATUSES = [
  'requested',
  'approved',
  'declined',
  'withdrawn',
] as const;
export type TournamentVenueHostStatus = (typeof TOURNAMENT_VENUE_HOST_STATUSES)[number];
export const TournamentVenueHostStatusSchema = z.enum(TOURNAMENT_VENUE_HOST_STATUSES);

/**
 * Marketplace sort options. F09 v2 (E31) adds `recommended` as the FIRST value
 * and the new default — verified organizers first, keyset
 * `(rank, start_date, created_at, id)`. Until the 2C producer ships, the API
 * treats an explicit `recommended` like the chronological default.
 */
export const TOURNAMENT_SORT_OPTIONS = [
  'recommended',
  'starting_soon',
  'newest',
  'nearest',
] as const;
export type TournamentSort = (typeof TOURNAMENT_SORT_OPTIONS)[number];
export const TournamentSortSchema = z.enum(TOURNAMENT_SORT_OPTIONS);

// ─── Bounds / constants ──────────────────────────────────────────────────────

export const TOURNAMENT_TITLE_MIN = 3;
export const TOURNAMENT_TITLE_MAX = 120;
export const TOURNAMENT_DESCRIPTION_MAX = 2000;
export const TOURNAMENT_DIVISION_NAME_MAX = 80;
export const TOURNAMENT_ANNOUNCEMENT_TITLE_MAX = 120;
export const TOURNAMENT_ANNOUNCEMENT_BODY_MAX = 2000;
export const TOURNAMENT_LOCATION_TEXT_MAX = 200;
/** Default refund deadline = `start_date − 7 days` when the organizer leaves it
 * NULL at publish (D7). */
export const TOURNAMENT_DEFAULT_REFUND_DEADLINE_DAYS_BEFORE_START = 7;
/** Skill-range bounds (1.0–7.0, F31 scale). */
export const TOURNAMENT_SKILL_MIN = 1.0;
export const TOURNAMENT_SKILL_MAX = 7.0;
/** Banner upload limits (D13 — 5 MB, jpeg/png/webp). */
export const TOURNAMENT_BANNER_MAX_BYTES = 5 * 1024 * 1024;
export const TOURNAMENT_BANNER_MIMES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type TournamentBannerMime = (typeof TOURNAMENT_BANNER_MIMES)[number];

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * F41 error vocabulary (Part A + Part B). Service maps to HTTP:
 *   - `*_NOT_FOUND` → 404
 *   - `TOURNAMENT_STALE` → 409 (If-Match mismatch)
 *   - `TOURNAMENT_REQUIRES_PRO_OR_APPROVAL` / `*_FORBIDDEN` → 403
 *   - `*_FULL` / `ALREADY_REGISTERED` / `SLOT`-type races → 409
 *   - everything else → 422
 *
 * `FEATURE_GATE_BLOCKED` is reused from F38's `SUBSCRIPTION_ERROR_CODES` — it is
 * NOT redeclared here.
 */
export const TOURNAMENT_ERROR_CODES = [
  // ── lifecycle / authz / gating ──
  'TOURNAMENT_NOT_FOUND',
  'DIVISION_NOT_FOUND',
  'REGISTRATION_NOT_FOUND',
  'TOURNAMENT_STALE',
  'TOURNAMENT_FORBIDDEN',
  'TOURNAMENT_REQUIRES_PRO_OR_APPROVAL',
  'TOURNAMENT_QUOTA_REACHED',
  'TOURNAMENT_FIELD_LOCKED',
  'TOURNAMENT_INVALID_TRANSITION',
  'TOURNAMENT_NOT_PUBLISHABLE',
  'TOURNAMENT_ALREADY_PUBLISHED',
  'ORGANIZER_NOT_APPROVED',
  // ── registration / capacity ──
  'REGISTRATION_NOT_OPEN',
  'TOURNAMENT_FULL',
  'DIVISION_FULL',
  'ALREADY_REGISTERED',
  'DIVISION_NOT_FOR_TEAMS',
  'DIVISION_NOT_FOR_SOLO',
  'TEAM_SPORT_MISMATCH',
  'NOT_TEAM_CAPTAIN',
  'SKILL_OUT_OF_RANGE_ACK_REQUIRED',
  'DIVISION_HAS_REGISTRATIONS',
  'BRACKET_ALREADY_GENERATED',
  // ── payment / refund ──
  'INVALID_PAYMENT_TRANSITION',
  'REGISTRATION_NOT_PAYABLE',
  'WITHDRAWAL_AFTER_DEADLINE',
  // ── hosting (§18.2) ──
  'HOST_NOT_FOUND',
  'HOST_NOT_APPROVED',
  'HOST_ALREADY_REQUESTED',
  'HOST_PRIMARY_REQUIRED',
  // ── in-play (Part B — shared with tournament-bracket.ts) ──
  'BRACKET_LOCKED',
  'BRACKET_NOT_GENERATED',
  'INSUFFICIENT_PARTICIPANTS',
  'LEAGUE_WINDOW_TOO_SHORT',
  'MATCH_NOT_FOUND',
  'MATCH_NOT_FINALIZABLE',
  'MATCH_ALREADY_FINAL',
  'SCORE_CORRECTION_FORBIDDEN',
  'INVALID_SCORE',
  // ── FIX 4.4 (journey audit D4) ──
  // A `cityCode` that is not a known city.
  'TOURNAMENT_INVALID_CITY',
  // `twoStage: true` — the bracket engine has no group → knockout structure yet.
  'BRACKET_FORMAT_UNSUPPORTED',
] as const;
export type TournamentErrorCode = (typeof TOURNAMENT_ERROR_CODES)[number];

// ─── Zod input schemas — tournament CRUD ─────────────────────────────────────

/**
 * The (un-refined) create base, kept standalone so the update schema can
 * `.partial()` it (a `ZodEffects` from `.refine` does not expose `.partial()` —
 * the F40 `PromotionInputBaseSchema` precedent). Post-publish field-locking is
 * enforced at the SERVICE layer (§4), so the update schema stays permissive.
 */
export const TournamentInputBaseSchema = z.object({
  title: z.string().min(TOURNAMENT_TITLE_MIN).max(TOURNAMENT_TITLE_MAX),
  sportName: z.string().min(1),
  description: z.string().max(TOURNAMENT_DESCRIPTION_MAX).nullable().optional(),
  startDate: z.string().datetime({ offset: true }),
  endDate: z.string().datetime({ offset: true }),
  registrationOpenAt: z.string().datetime({ offset: true }),
  registrationCloseAt: z.string().datetime({ offset: true }),
  refundDeadlineAt: z.string().datetime({ offset: true }).nullable().optional(),
  totalCapacity: z.number().int().positive().nullable().optional(),
  prizePoolMinor: z.number().int().min(0).nullable().optional(),
  prizePoolCurrency: z.string().length(3).default('USD'),
  inviteOnly: z.boolean().default(false),
  /**
   * FIX 4.4 (D-D4) — the headline city for a tournament with no venue host
   * (free-text location). A primary venue host's city and coordinates win;
   * otherwise this city's centre fills the coordinates.
   */
  cityCode: z.string().min(1).max(80).nullable().optional(),
});

/**
 * `POST /me/tournaments` body. Cross-field invariants:
 *   1. `endDate >= startDate`.
 *   2. `registrationOpenAt < registrationCloseAt <= startDate`.
 *   3. `refundDeadlineAt` (when set) must be on-or-before `startDate`.
 */
export const CreateTournamentInputSchema = TournamentInputBaseSchema.strict()
  .refine((v) => new Date(v.endDate).getTime() >= new Date(v.startDate).getTime(), {
    message: 'endDate must be on or after startDate',
    path: ['endDate'],
  })
  .refine(
    (v) =>
      new Date(v.registrationOpenAt).getTime() < new Date(v.registrationCloseAt).getTime() &&
      new Date(v.registrationCloseAt).getTime() <= new Date(v.startDate).getTime(),
    {
      message: 'registrationOpenAt < registrationCloseAt <= startDate is required',
      path: ['registrationCloseAt'],
    },
  )
  .refine(
    (v) =>
      v.refundDeadlineAt === undefined ||
      v.refundDeadlineAt === null ||
      new Date(v.refundDeadlineAt).getTime() <= new Date(v.startDate).getTime(),
    { message: 'refundDeadlineAt must be on or before startDate', path: ['refundDeadlineAt'] },
  );
export type CreateTournamentInput = z.infer<typeof CreateTournamentInputSchema>;

/**
 * `PATCH /me/tournaments/:id` body. Permissive `.partial()` of the base —
 * post-publish field-locking (sport/format/location lock; window extend-only)
 * is enforced at the SERVICE layer (§4), not by the schema.
 */
export const UpdateTournamentInputSchema = TournamentInputBaseSchema.partial().strict();
export type UpdateTournamentInput = z.infer<typeof UpdateTournamentInputSchema>;

/** `POST /me/tournaments/:id/publish` body (tier-gate + quota + snapshot fire). */
export const PublishTournamentInputSchema = z.object({}).strict();
export type PublishTournamentInput = z.infer<typeof PublishTournamentInputSchema>;

/**
 * `POST /me/tournaments/:id/cancel` (+ admin mirror) body. Full refund cascade
 * to all registrations + unconditional promo reversal (D7). Mandatory reason.
 */
export const CancelTournamentInputSchema = z
  .object({
    reason: z.string().min(1).max(500),
  })
  .strict();
export type CancelTournamentInput = z.infer<typeof CancelTournamentInputSchema>;

/**
 * `POST /admin/tournaments` body (D19). Admin creates on behalf of a chosen
 * organizer `b2b_account`; the tier-gate is bypassed + the listing fee waived at
 * publish + the quota skipped, with a mandatory audited reason (M13). Carries the
 * full create payload plus the target organizer id.
 */
export const AdminCreateTournamentInputSchema = z
  .object({
    organizerB2bAccountId: z.string().uuid(),
    reason: z.string().min(1).max(500),
    // FIX journey audit 2.7 (§10.8-F16f): the same date rules as organizer create.
    tournament: CreateTournamentInputSchema,
  })
  .strict();
export type AdminCreateTournamentInput = z.infer<typeof AdminCreateTournamentInputSchema>;

/** `POST /admin/tournaments/:id/suspend` body (D13 — orthogonal admin flag). */
export const SuspendTournamentInputSchema = z
  .object({
    reason: z.string().min(1).max(500),
  })
  .strict();
export type SuspendTournamentInput = z.infer<typeof SuspendTournamentInputSchema>;

/** `POST /admin/tournaments/:id/force-refund` body. */
export const ForceRefundTournamentInputSchema = z
  .object({
    reason: z.string().min(1).max(500),
  })
  .strict();
export type ForceRefundTournamentInput = z.infer<typeof ForceRefundTournamentInputSchema>;

// ─── Zod input schemas — divisions ───────────────────────────────────────────

/**
 * `POST /me/tournaments/:id/divisions` body. `skillMin <= skillMax` (both in
 * 1.0–7.0); the points scheme + seeding default at the service layer.
 */
export const CreateTournamentDivisionInputSchema = z
  .object({
    name: z.string().min(1).max(TOURNAMENT_DIVISION_NAME_MAX),
    registrationKind: TournamentRegistrationKindSchema,
    format: TournamentDivisionFormatSchema,
    capacity: z.number().int().positive(),
    feeMinor: z.number().int().min(0).default(0),
    feeCurrency: z.string().length(3).default('USD'),
    skillMin: z.number().min(TOURNAMENT_SKILL_MIN).max(TOURNAMENT_SKILL_MAX).nullable().optional(),
    skillMax: z.number().min(TOURNAMENT_SKILL_MIN).max(TOURNAMENT_SKILL_MAX).nullable().optional(),
    genderCategory: TournamentGenderCategorySchema.nullable().optional(),
    ageCategory: z.string().max(40).nullable().optional(),
    ordinal: z.number().int().min(0).default(0),
    thirdPlaceMatch: z.boolean().default(false),
    pointsWin: z.number().int().min(0).max(100).default(3),
    pointsDraw: z.number().int().min(0).max(100).default(1),
    pointsLoss: z.number().int().min(0).max(100).default(0),
    seedingMethod: TournamentSeedingMethodSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.skillMin === undefined ||
      v.skillMin === null ||
      v.skillMax === undefined ||
      v.skillMax === null ||
      v.skillMin <= v.skillMax,
    { message: 'skillMin must be <= skillMax', path: ['skillMax'] },
  );
export type CreateTournamentDivisionInput = z.infer<typeof CreateTournamentDivisionInputSchema>;

/**
 * `PATCH /me/tournaments/:id/divisions/:divisionId` body. Permissive — the
 * fee/capacity-down/skill lock after the first confirmed registration is enforced
 * at the SERVICE layer (§4).
 */
export const UpdateTournamentDivisionInputSchema = z
  .object({
    name: z.string().min(1).max(TOURNAMENT_DIVISION_NAME_MAX),
    format: TournamentDivisionFormatSchema,
    capacity: z.number().int().positive(),
    feeMinor: z.number().int().min(0),
    feeCurrency: z.string().length(3),
    skillMin: z.number().min(TOURNAMENT_SKILL_MIN).max(TOURNAMENT_SKILL_MAX).nullable(),
    skillMax: z.number().min(TOURNAMENT_SKILL_MIN).max(TOURNAMENT_SKILL_MAX).nullable(),
    genderCategory: TournamentGenderCategorySchema.nullable(),
    ageCategory: z.string().max(40).nullable(),
    ordinal: z.number().int().min(0),
    thirdPlaceMatch: z.boolean(),
    pointsWin: z.number().int().min(0).max(100),
    pointsDraw: z.number().int().min(0).max(100),
    pointsLoss: z.number().int().min(0).max(100),
    seedingMethod: TournamentSeedingMethodSchema.nullable(),
  })
  .partial()
  .strict();
export type UpdateTournamentDivisionInput = z.infer<typeof UpdateTournamentDivisionInputSchema>;

// ─── Zod input schemas — venue hosting (§18.2, D20) ──────────────────────────

/**
 * `POST /me/tournaments/:id/hosts` body. Exactly one of `venueId` (a claimed BraveMove
 * venue → inserts `requested`, the venue decides) or `locationText` (a free-text
 * location → inserts `approved`). `isPrimary` designates the headline location.
 */
export const AddTournamentVenueHostInputSchema = z
  .object({
    venueId: z.string().uuid().nullable().optional(),
    locationText: z.string().min(1).max(TOURNAMENT_LOCATION_TEXT_MAX).nullable().optional(),
    isPrimary: z.boolean().default(false),
  })
  .strict()
  .refine(
    (v) => {
      const hasVenue = v.venueId !== undefined && v.venueId !== null;
      const hasText = v.locationText !== undefined && v.locationText !== null;
      return hasVenue !== hasText; // exactly one
    },
    { message: 'exactly one of venueId or locationText is required' },
  );
export type AddTournamentVenueHostInput = z.infer<typeof AddTournamentVenueHostInputSchema>;

/** `POST /me/venues/:venueId/hosting-requests/:hostId/approve` body. */
export const ApproveTournamentVenueHostInputSchema = z.object({}).strict();
export type ApproveTournamentVenueHostInput = z.infer<typeof ApproveTournamentVenueHostInputSchema>;

/** `POST /me/venues/:venueId/hosting-requests/:hostId/decline` body. */
export const DeclineTournamentVenueHostInputSchema = z
  .object({
    declineReason: z.string().min(1).max(500).optional(),
  })
  .strict();
export type DeclineTournamentVenueHostInput = z.infer<typeof DeclineTournamentVenueHostInputSchema>;

// ─── Zod input schemas — registration ────────────────────────────────────────

/**
 * `POST /me/tournament-registrations` body (the capacity-atomic register flow,
 * §2.10). Exactly one of `teamId` (a team entry into a `team` division — only the
 * captain may submit) is set for a team registration; absent → a solo entry.
 * `outOfRangeAck` clears the advisory skill-range soft gate (D11/§2.7). The
 * `Idempotency-Key` HTTP header (not a body field) drives replay dedup.
 */
export const RegisterTournamentInputSchema = z
  .object({
    divisionId: z.string().uuid(),
    teamId: z.string().uuid().optional(),
    appliedPromotionCode: z.string().min(1).max(32).optional(),
    outOfRangeAck: z.boolean().default(false),
    /** FIX 4.4 (N5) — required for an invite-only tournament: its share-link token. */
    inviteToken: z.string().min(1).max(200).optional(),
  })
  .strict();
export type RegisterTournamentInput = z.infer<typeof RegisterTournamentInputSchema>;

/** `POST /me/tournament-registrations/:id/withdraw` body. Refund-deadline logic +
 * seat free + promo reversal handled at the service layer (D7). */
export const WithdrawTournamentRegistrationInputSchema = z
  .object({
    reason: z.string().max(500).optional(),
  })
  .strict();
export type WithdrawTournamentRegistrationInput = z.infer<
  typeof WithdrawTournamentRegistrationInputSchema
>;

/**
 * `POST /me/tournaments/:id/registrants/:regId/mark-paid` body (§2.4 — the
 * organizer marks a registration paid off-platform: `unpaid → paid`, flips the
 * registration `pending_payment → confirmed`). Idempotent via the payment-status
 * transition + an `Idempotency-Key` header. `paidAmountMinor` records what was
 * collected (defaults to the registration's `net_fee_amount_minor` server-side).
 */
export const MarkTournamentRegistrationPaidInputSchema = z
  .object({
    paidAmountMinor: z.number().int().min(0).optional(),
    note: z.string().max(500).optional(),
  })
  .strict();
export type MarkTournamentRegistrationPaidInput = z.infer<
  typeof MarkTournamentRegistrationPaidInputSchema
>;

/**
 * `POST /me/tournaments/:id/registrants/mark-paid` body (bulk mark-paid — the
 * organizer marks several registrations paid in one request). Each id runs the
 * same single-mark logic; the response carries per-id results.
 */
export const BulkMarkTournamentRegistrationsPaidInputSchema = z
  .object({
    registrationIds: z.array(z.string().uuid()).min(1).max(200),
    note: z.string().max(500).optional(),
  })
  .strict();
export type BulkMarkTournamentRegistrationsPaidInput = z.infer<
  typeof BulkMarkTournamentRegistrationsPaidInputSchema
>;

/** One per-id result from a bulk mark-paid call. */
export type BulkMarkPaidResultItem = {
  readonly registrationId: string;
  readonly ok: boolean;
  /** The updated registration when `ok`; null on failure. */
  readonly registration: RegistrationDetail | null;
  /** The `TournamentErrorCode` when `ok` is false; null otherwise. */
  readonly errorCode: string | null;
};

/** The full bulk mark-paid response. */
export type BulkMarkPaidResult = {
  readonly results: ReadonlyArray<BulkMarkPaidResultItem>;
  readonly paidCount: number;
  readonly failedCount: number;
};

/** `POST /me/tournaments/:id/registrants/:regId/cancel` body (organizer frees a
 * no-show seat — an audited organizer action, §2.4). */
export const CancelTournamentRegistrationInputSchema = z
  .object({
    reason: z.string().max(500).optional(),
  })
  .strict();
export type CancelTournamentRegistrationInput = z.infer<
  typeof CancelTournamentRegistrationInputSchema
>;

// ─── Zod input schemas — announcements + alerts + organizer approval ─────────

/** `POST /me/tournaments/:id/announcements` body (broadcast to registrants). */
export const CreateTournamentAnnouncementInputSchema = z
  .object({
    title: z.string().min(1).max(TOURNAMENT_ANNOUNCEMENT_TITLE_MAX),
    body: z.string().min(1).max(TOURNAMENT_ANNOUNCEMENT_BODY_MAX),
    audience: TournamentAnnouncementAudienceSchema.default('all_registrants'),
  })
  .strict();
export type CreateTournamentAnnouncementInput = z.infer<
  typeof CreateTournamentAnnouncementInputSchema
>;

/** `POST /me/tournament-alerts` body (per-sport "near me" opt-in, §2.8). */
export const TournamentAlertSubscriptionInputSchema = z
  .object({
    sportName: z.string().min(1),
  })
  .strict();
export type TournamentAlertSubscriptionInput = z.infer<
  typeof TournamentAlertSubscriptionInputSchema
>;

/**
 * `POST /admin/organizers/:b2bAccountId/approve` body (§18.1 — admin approves a
 * federation/league organizer partner; free listings, no quota). Mandatory reason.
 */
export const ApproveOrganizerInputSchema = z
  .object({
    organizerType: OrganizerTypeSchema.optional(),
    reason: z.string().min(1).max(500),
  })
  .strict();
export type ApproveOrganizerInput = z.infer<typeof ApproveOrganizerInputSchema>;

// ─── Zod input schemas — banner upload (D8) ──────────────────────────────────

/**
 * `POST /me/tournaments/:id/banner/upload-url` body. Issues a 15-min presigned
 * PUT to the `tournament-banners` bucket. Mirrors F12 venue-photo / F29 team-
 * photo upload (request URL → client PUT → finalize).
 */
export const TournamentBannerMimeSchema = z.enum(TOURNAMENT_BANNER_MIMES);

export const RequestTournamentBannerUploadInputSchema = z
  .object({
    mime: TournamentBannerMimeSchema,
    sizeBytes: z.number().int().min(1).max(TOURNAMENT_BANNER_MAX_BYTES),
  })
  .strict();
export type RequestTournamentBannerUploadInput = z.infer<
  typeof RequestTournamentBannerUploadInputSchema
>;

/** Presigned PUT response (the client uploads directly to S3 then calls finalize). */
export type TournamentBannerUploadUrl = {
  readonly url: string;
  readonly s3Key: string;
  readonly expiresAt: string;
  readonly headers: Readonly<Record<string, string>>;
};

/** `POST /me/tournaments/:id/banner` body — finalize after the client PUTs the bytes. */
export const FinalizeTournamentBannerInputSchema = z
  .object({
    s3Key: z.string().min(1).max(512),
  })
  .strict();
export type FinalizeTournamentBannerInput = z.infer<typeof FinalizeTournamentBannerInputSchema>;

// ─── Read-side projections ───────────────────────────────────────────────────

/** One `organizer_profiles` row (§2.1 / §18.1). */
export type OrganizerProfile = {
  readonly b2bAccountId: string;
  readonly organizerType: OrganizerType;
  readonly status: OrganizerProfileStatus;
  readonly approvedAt: string | null;
  readonly approvedByAdminId: string | null;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly website: string | null;
  readonly description: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * F09 v2 — the account holds the organizer tick
   * (`b2b_accounts.verification_status = 'verified'` AND `account_kind = 'organizer'`).
   * A venue- or club-kind organizer account never reports an organizer tick (E39).
   */
  readonly verified: boolean;
};

/**
 * An `organizer_profiles` row joined to its owning `b2b_accounts` row, for the
 * admin pending-approval queue (`GET /admin/organizers`). The `legalBusinessName`
 * / `displayName` come from the b2b_account so the admin can identify the partner.
 */
export type OrganizerProfileWithAccount = OrganizerProfile & {
  readonly legalBusinessName: string;
  readonly accountDisplayName: string;
};

/** Paginated admin organizers list response. */
export type OrganizerListResponse = {
  readonly data: ReadonlyArray<OrganizerProfileWithAccount>;
  readonly nextCursor: string | null;
};

/**
 * `GET /me/tournaments/organizer-tier?b2bAccountId=` — the §18.1 publish gate as
 * the server enforces it. An account has no subscription tier of its own: an
 * admin-approved organizer publishes free with no tier gate, otherwise the
 * account needs an owned Pro+ venue and `effectiveTierCode` is the best such
 * venue's tier (`null` = none, so publish 403s `TOURNAMENT_REQUIRES_PRO_OR_APPROVAL`).
 */
export type OrganizerPublishGate = {
  readonly b2bAccountId: string;
  readonly approvedOrganizer: boolean;
  readonly effectiveTierCode: SubscriptionTierCode | null;
  readonly canPublish: boolean;
};

/**
 * One admin roster entry — a `tournament_registrations` row joined to its
 * registrant identity (solo user display name + email, OR team name). Surfaced
 * by `GET /admin/tournaments/:id/registrants` so the admin detail page shows the
 * actual roster, not just per-division counts.
 */
export type RegistrantRosterEntry = RegistrationDetail & {
  readonly divisionName: string | null;
  readonly registrantUserDisplayName: string | null;
  readonly registrantUserEmail: string | null;
  readonly registrantTeamName: string | null;
};

/**
 * One organizer roster row (`GET /me/tournaments/:id/registrants` and the
 * dashboard): the registration plus the team name or the player's display name.
 * No email: organizers see who registered, not how to reach them off-platform.
 */
export type OrganizerRegistrant = RegistrationDetail & {
  readonly registrantName: string | null;
};

/**
 * Tournament banner variant URLs (D8 — wide 16:9 thumb/card/hero WebP set,
 * produced by the Sharp pipeline at finalize). Persisted as the `banner_variants`
 * jsonb on the tournament row alongside `banner_image_s3_key`.
 */
export type TournamentBannerVariants = {
  readonly thumb_url: string;
  readonly card_url: string;
  readonly hero_url: string;
};

/**
 * A division row + its live atomic `registeredCount`. The skill range is advisory
 * (D11). `format`/`seedingMethod`/champion are the in-play columns (Part B).
 */
export type DivisionDetail = {
  readonly id: string;
  readonly tournamentId: string;
  readonly name: string;
  readonly registrationKind: TournamentRegistrationKind;
  readonly format: TournamentDivisionFormat;
  readonly capacity: number;
  readonly registeredCount: number;
  readonly feeMinor: number;
  readonly feeCurrency: string;
  readonly skillMin: number | null;
  readonly skillMax: number | null;
  readonly genderCategory: TournamentGenderCategory | null;
  readonly ageCategory: string | null;
  readonly ordinal: number;
  readonly thirdPlaceMatch: boolean;
  readonly pointsWin: number;
  readonly pointsDraw: number;
  readonly pointsLoss: number;
  readonly seedingMethod: TournamentSeedingMethod | null;
  readonly bracketGeneratedAt: string | null;
  readonly championRegistrationId: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** One approved/requested venue host (§18.2). Exactly one of venueId/locationText. */
export type VenueHost = {
  readonly id: string;
  readonly tournamentId: string;
  readonly venueId: string | null;
  readonly venueName: string | null;
  readonly locationText: string | null;
  readonly isPrimary: boolean;
  readonly status: TournamentVenueHostStatus;
  readonly requestedAt: string;
  readonly decidedAt: string | null;
  readonly decidedByUserId: string | null;
  readonly declineReason: string | null;
  readonly countryCode: string;
};

/**
 * Marketplace / list row. `primaryCityCode`/`lat`/`lng` are the denormalized
 * headline location (copied from the primary host). `format` is the headline
 * snapshot. `sponsored` marks an F39-promoted card (renders the "Sponsored" Badge).
 */
export type TournamentSummary = {
  readonly id: string;
  readonly organizerB2bAccountId: string;
  readonly organizerKind: TournamentOrganizerKind;
  /** F09 v2 — the organizer account's display name. */
  readonly organizerDisplayName: string | null;
  /**
   * F09 v2 (E32/E39) — the organizer account holds a club/organizer tick.
   * Always false for venue-kind organizer accounts.
   */
  readonly organizerVerified: boolean;
  readonly title: string;
  readonly sportName: string;
  readonly format: TournamentHeadlineFormat;
  readonly status: TournamentStatus;
  readonly bannerImageUrl: string | null;
  readonly bannerVariants: TournamentBannerVariants | null;
  readonly startDate: string;
  readonly endDate: string;
  readonly registrationOpenAt: string;
  readonly registrationCloseAt: string;
  readonly primaryCityCode: string | null;
  readonly lat: string | null;
  readonly lng: string | null;
  readonly totalCapacity: number | null;
  readonly totalRegisteredCount: number;
  readonly prizePoolMinor: number | null;
  readonly prizePoolCurrency: string;
  readonly inviteOnly: boolean;
  readonly sponsored: boolean;
  readonly countryCode: string;
  readonly createdAt: string;
};

/**
 * Full tournament detail (organizer + public viewer-aware). Bundles divisions +
 * approved hosts. `listingFeeMinor`/`subscriptionTierCodeAtPublish` are the
 * publish snapshots; the orthogonal `suspendedAt` trio is null unless an admin
 * has suspended it; cancellation fields are null unless cancelled.
 */
export type TournamentDetail = {
  readonly id: string;
  readonly organizerB2bAccountId: string;
  readonly organizerKind: TournamentOrganizerKind;
  /** F09 v2 — the organizer account's display name. */
  readonly organizerDisplayName: string | null;
  /** F09 v2 (E32/E39) — organizer tick (false for venue-kind organizers). */
  readonly organizerVerified: boolean;
  readonly createdByUserId: string;
  readonly title: string;
  readonly sportName: string;
  readonly format: TournamentHeadlineFormat;
  readonly description: string | null;
  readonly bannerImageUrl: string | null;
  readonly bannerVariants: TournamentBannerVariants | null;
  readonly startDate: string;
  readonly endDate: string;
  readonly registrationOpenAt: string;
  readonly registrationCloseAt: string;
  readonly refundDeadlineAt: string | null;
  readonly primaryCityCode: string | null;
  readonly lat: string | null;
  readonly lng: string | null;
  readonly totalCapacity: number | null;
  readonly totalRegisteredCount: number;
  readonly prizePoolMinor: number | null;
  readonly prizePoolCurrency: string;
  readonly inviteOnly: boolean;
  /**
   * FIX 4.4 (D-TOUR) — the share-link token of an invite-only tournament. Set
   * only on the organizer's own read (`GET /me/tournaments/:id`); never on a
   * public read.
   */
  readonly inviteToken?: string | null;
  readonly status: TournamentStatus;
  readonly listingFeeMinor: number;
  readonly listingFeeCurrency: string;
  readonly subscriptionTierCodeAtPublish: SubscriptionTierCode | null;
  readonly billedStatementId: string | null;
  readonly publishedAt: string | null;
  readonly cancelledAt: string | null;
  readonly cancellationReason: string | null;
  readonly suspendedAt: string | null;
  readonly suspendedByAdminId: string | null;
  readonly suspensionReason: string | null;
  readonly divisions: ReadonlyArray<DivisionDetail>;
  readonly hosts: ReadonlyArray<VenueHost>;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * One `tournament_registrations` row (a real F41 registration — `tournamentId`
 * set). Exactly one of `registrantUserId` (solo) / `registrantTeamId` (team).
 * `paymentStatus` reuses F17's `PaymentStatus`. The promo snapshot lowers
 * `netFeeAmountMinor` (what the registrant owes off-platform).
 */
export type RegistrationDetail = {
  readonly id: string;
  readonly tournamentId: string;
  readonly divisionId: string;
  readonly registrantUserId: string | null;
  readonly registrantTeamId: string | null;
  readonly submitterUserId: string | null;
  readonly status: TournamentRegistrationLifecycleStatus;
  readonly paymentStatus: PaymentStatus;
  readonly feeAmountMinor: number;
  readonly promotionId: string | null;
  readonly discountAmountMinor: number;
  readonly netFeeAmountMinor: number;
  readonly paidAmountMinor: number | null;
  readonly registeredAt: string | null;
  readonly withdrawnAt: string | null;
  readonly withdrawnByUserId: string | null;
  readonly refundEligible: boolean | null;
  readonly outOfRangeAck: boolean;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * `GET /me/tournament-registrations` (+ `/:id`) row — the registration plus the
 * tournament + division context a "My tournaments" list renders (title, sport,
 * dates, lifecycle status, division name), so clients don't fetch every
 * tournament detail (which also 404s for an invite-only tournament without its
 * token). Additive over `RegistrationDetail`.
 */
export type MyTournamentRegistration = RegistrationDetail & {
  readonly tournament: {
    readonly id: string;
    readonly title: string;
    readonly sportName: string;
    readonly status: TournamentStatus;
    readonly startDate: string;
    readonly endDate: string;
  };
  readonly divisionName: string | null;
};

/** One `tournament_announcements` row (append-only). */
export type AnnouncementItem = {
  readonly id: string;
  readonly tournamentId: string;
  readonly authorUserId: string;
  readonly title: string;
  readonly body: string;
  readonly audience: TournamentAnnouncementAudience;
  readonly createdAt: string;
};

/**
 * FIX 5.7 — the admin view of an announcement (`GET /admin/tournaments/:id/
 * announcements`): hidden ones included, with the F43 hide stamp. Readers never
 * see hidden announcements.
 */
export type AdminAnnouncementItem = AnnouncementItem & {
  readonly hiddenAt: string | null;
  readonly hiddenReason: string | null;
};

/** Per-(user × sport) tournament alert subscription row (§2.8). */
export type TournamentAlertSubscription = {
  readonly userId: string;
  readonly sportName: string;
  readonly countryCode: string;
  readonly createdAt: string;
};

// ─── Sponsored read interface (F39 — mirrors F40 PromoLookupForSponsored) ─────

/**
 * Thin reference to a published, non-`invite_only` tournament, surfaced for F39
 * featured placements to render a sponsored-tournament card. F41 ships the
 * implementation; F39 consumes the INTERFACE only (instantiated-but-unwired —
 * `void` in server.ts — until F39 adds a `tournament` placement scope, per M3).
 */
export type SponsoredTournamentRef = {
  readonly tournamentId: string;
  readonly title: string;
  readonly sportName: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly bannerImageUrl: string | null;
  readonly citySlug: string | null;
  readonly deepLinkPath: string;
  readonly status: TournamentStatus;
  /** F09 v2 — organizer display name. */
  readonly organizerDisplayName: string | null;
  /** F09 v2 (E32/E39) — organizer tick. */
  readonly organizerVerified: boolean;
};

/**
 * The published-for-F39 read contract. Featured placements call this to surface a
 * sponsored tournament card; returns null when the tournament is not eligible
 * (not `published`/`registration_open`, or `invite_only`). F41 ships the
 * implementation; F39 consumes the INTERFACE only (never `new`s the concrete
 * service).
 */
export interface TournamentLookupForSponsored {
  getSponsoredTournament(tournamentId: string): Promise<SponsoredTournamentRef | null>;
}
